export type GenericJob<JobType extends string, LogType extends unknown, LogData extends unknown> = {
    id: string;
    type: JobType;
    data?: LogData;
    /** 'kubeJobCreated' is set while the kube pod is being scheduled. */
    state: 'idle' | 'kubeJobCreated' | 'started' | 'ended' | 'error';
    created_at: string;
    started_at?: string;
    ended_at?: string;
    deleted_at?: string;
    progress: number;
    page: number;
    logs: {
        infos: LogType[];
        warnings: LogType[];
        errors: LogType[];
    };
    attachmentCompletePath?: string;
    filename?: string;
    user?: { username?: string; attributes: { company: string } };
    username?: string;
};

export enum CatImportJobDataFields {
    CREATED_NODES = 'createdNodes',
    ITEMS_MODIFIED_PRICING = 'itemsWithPricingChanges',
    ITEMS_NON_RELEVANT_MODIFIED = 'itemsWithNonRelevantChanges',
    ITEMS_NOT_MODIFIED = 'itemsNotModified',
    ITEMS_DISABLED = 'itemsDisabled',
    UPDATED_GLS = 'updatedGls',
    ALIGNED_GLS = 'alignedGls',
    ALIGNED_MODELS = 'alignedModels',
}

/** Fields the CAT import writes on top of the shared import ones. */
export enum CatImportExtraJobDataFields {
    CREATED_FAMILIES = 'createdFamilies',
    CREATED_MODELS = 'createdModels',
    ITEMS_BLOCKED_PRICE_UPDATE = 'itemsWithBlockedPriceUpdate',
    ITEMS_BLOCKED_NOT_CHARGED_UPDATE = 'itemsWithBlockedNotChargedUpdate',
}

export enum AlignPricesJobDataFields {
    UPDATED_GLS = 'updatedGls',
    UPDATED_MODELS = 'updatedModels',
}

export enum RestartTranslationJobDataFields {
    ITEMS_MODIFIED = 'itemsModified',
}

export enum PublishFamilyJobDataFields {
    CREATED_NODES = 'createdNodes',
}

export enum CloseFamilyJobDataFields {
    REMOVED_SCHEDE = 'removedSchede',
}

export enum ReopenFamilyJobDataFields {
    REPUBLISHED_SCHEDE = 'republishedSchede',
}

export enum DuplicateNodeJobDataFields {
    CREATED_NODES = 'createdNodes',
}

export enum SapHierarchyJobDataFields {
    CREATED_NODES = 'createdNodes',
}

export enum ImportIdpUsersJobDataFields {
    CREATED_NODES = 'createdNodes',
    ITEMS_MODIFIED = 'itemsModified',
    ITEMS_DISABLED = 'itemsDisabled',
    ITEMS_NOT_MODIFIED = 'itemsNotModified',
}

export type JobDataItem = {
    hierarchyId: string;
    nodeId: string;
    code: string;
    changes: string[];
    isReplacement?: boolean;
    /**
     * CGT model of a prototype row. Prototypes are edited on their own page, addressed by model,
     * so the drill-down opens it there instead of the generic node editor.
     */
    prototypeModelId?: string;
};

export type DetailSummaryKey =
    | DetailType.PRICING_CHANGES
    | DetailType.NON_RELEVANT_CHANGES
    | DetailType.DISABLED
    | DetailType.CREATED
    | DetailType.CREATED_MODIFIED
    | DetailType.NON_MODIFIED
    | DetailType.ALIGNED_MODELS
    | DetailType.ALIGNED_GLS
    | DetailType.UPDATED_GLS
    | DetailType.UPDATED_MODELS
    | DetailType.NEW_FAMILIES_ADDED
    | DetailType.REALIGNED_FAMILIES
    | DetailType.CREATED_FAMILIES
    | DetailType.CREATED_MODELS
    | DetailType.REMOVED_SCHEDE
    | DetailType.REPUBLISHED_SCHEDE
    | DetailType.BLOCKED_PRICE_UPDATE
    | DetailType.BLOCKED_NOT_CHARGED_UPDATE
    | DetailType.FAILURES
    | DetailType.WARNINGS;

export type Detail = Omit<JobDataItem, 'changes'> & { change?: string | null };

export type DetailSummaryTypes = Record<
    DetailSummaryKey,
    {
        descriptionComponent: JSX.Element;
        details: string[] | Detail[];
    }
>;

type CatImportJobDialogData = Record<CatImportJobDataFields, JobDataItem[]> &
    Partial<Record<CatImportExtraJobDataFields, JobDataItem[]>>;
type ImportJobDialogBaseData = Record<CatImportJobDataFields, JobDataItem[]>;
export type ImportJobDialogCGTData = ImportJobDialogBaseData & {
    cgtRecap: Record<CatImportJobDataFields, JobDataItem[]>;
};
export type ImportJobDialogData = ImportJobDialogBaseData | ImportJobDialogCGTData;
type RestartTranslationJobData = Record<RestartTranslationJobDataFields, string[]>;
export type AlignPricesJobData = Record<AlignPricesJobDataFields, string[]>;
export type PublishFamilyJobData = Record<PublishFamilyJobDataFields, JobDataItem[]>;
export type CloseFamilyJobData = Record<CloseFamilyJobDataFields, JobDataItem[]>;
export type ReopenFamilyJobData = Record<ReopenFamilyJobDataFields, JobDataItem[]>;

export type DuplicateNodeJobData = Record<DuplicateNodeJobDataFields, JobDataItem[]>;

export type ImportIdpUsersJobData = Record<ImportIdpUsersJobDataFields, JobDataItem[]> & {
    hierarchyId: string;
    /** True when the job only reported the differences without writing them. */
    dryRun: boolean;
    idpUserCount: number;
    reEnabledUsernames: string[];
    skippedUsernames: string[];
    logIssues: string[];
};

export type SapHierarchyJobData = {
    createdNodes: { hierarchyId: string; nodeId: string; code: string }[];
    newFamiliesAdded: { code: string; description: string }[];
    /** Nodes whose family pointed at the wrong option and was rewritten onto the right one. */
    realignedFamilies?: {
        hierarchyId: string;
        nodeId: string;
        code: string;
        previous: string;
        current: string;
    }[];
};

export type CatJobType = GenericJob<'CatImport', string, CatImportJobDialogData>;
type ImportJobType = GenericJob<'import', string, ImportJobDialogData>;
type PublishJobType = GenericJob<'Publish', string, unknown>;
type ExportXlsx = GenericJob<'ExportXlsx', string, unknown>;
type ExportHistory = GenericJob<'ExportHistory', string, unknown>;
type RestartTranslation = GenericJob<'restart-translation', string, RestartTranslationJobData>;
type AlignPrices = GenericJob<'AlignPrices', string, AlignPricesJobData>;
type ZeusCoolsalesDiff = GenericJob<'zeus-coolsales-diff', string, unknown>;
type PublishFamily = GenericJob<'publish-family', string, PublishFamilyJobData>;
type CloseFamily = GenericJob<'close-family', string, CloseFamilyJobData>;
type ReopenFamily = GenericJob<'reopen-family', string, ReopenFamilyJobData>;
type DuplicateNode = GenericJob<'duplicate-node', string, DuplicateNodeJobData>;
type SapHierarchyImport = GenericJob<'cgt-import-sap-hierarchy', string, SapHierarchyJobData>;
type ImportIdpUsers = GenericJob<'import-idp-users', string, ImportIdpUsersJobData>;

export type Jobs =
    | CatJobType
    | ImportJobType
    | PublishJobType
    | ExportXlsx
    | RestartTranslation
    | ExportHistory
    | AlignPrices
    | ZeusCoolsalesDiff
    | PublishFamily
    | CloseFamily
    | ReopenFamily
    | DuplicateNode
    | SapHierarchyImport
    | ImportIdpUsers;

export type JobData = { jobs: Jobs[]; count: number; hasNextPage: boolean };

// The values double as the drill-down key and as the BaseTable column id prefix
// (_.camelCase(detailType) in JobDetails), so they must stay stable. Display text comes from
// DETAIL_TYPE_LABELS instead.
export enum DetailType {
    OVERVIEW = 'overview',
    CREATED = 'Created',
    CREATED_MODIFIED = 'Created and Modified',
    PRICING_CHANGES = 'Pricing Changes',
    NON_RELEVANT_CHANGES = 'Non Relevant Changes',
    NON_MODIFIED = 'Non modified',
    DISABLED = 'Disabled',
    ALIGNED_MODELS = 'Aligned Models',
    ALIGNED_GLS = 'Aligned Gls',
    UPDATED_GLS = 'Updated Gls',
    UPDATED_MODELS = 'Updated Models',
    NEW_FAMILIES_ADDED = 'New Families Added',
    REALIGNED_FAMILIES = 'Realigned Families',
    CREATED_FAMILIES = 'Created Families',
    CREATED_MODELS = 'Created Models',
    REMOVED_SCHEDE = 'Removed Schede',
    REPUBLISHED_SCHEDE = 'Republished Schede',
    BLOCKED_PRICE_UPDATE = 'Blocked Price Update',
    BLOCKED_NOT_CHARGED_UPDATE = 'Blocked Not Charged Update',
    FAILURES = 'Failures',
    WARNINGS = 'Warnings',
}

export enum CounterType {
    SUCCESS = 'success',
    ERROR = 'error',
    WARNING = 'warning',
    UPDATED = 'updated',
    INFO = 'info',
}
